import React, {useState, useEffect, useRef, useContext, forwardRef} from "react";
import classNames from "classnames";
import styles from "./Toolbar.scss";

import { getVisibleSelectionRect } from "draft-js";
import { ToolbarItemGroup } from "./ToolbarItemGroup";

import { BlockTypeAction } from "./actions/BlockTypeAction";
import { BlockStyleAction } from "./actions/BlockStyleAction";
import { InlineStyleAction } from "./actions/InlineStyleAction";
import { CustomAction } from "./actions/CustomAction";
import { toolbarActionMap } from "./actionMap";
import { LinkEdit } from "./custom/LinkEdit";
import { TextColorEdit } from "./custom/TextColorEdit";
import { MarkupEditorContext } from "../MarkupEditorContext";
import { InsertSymbolEdit } from "./custom/InsertSymbolEdit";
import {InsertDash} from "./custom/InsertDash";

export const Toolbar = forwardRef(({ visible, editorState, onChange, getContainerEl }, toolbarRef) => {
    let updatePositionTimeout;
    const { actions, excludeActions } = useContext(MarkupEditorContext);
    const [toolbarStyle, setToolbarStyle] = useState({});
    const [editingEntity, setEditingEntity] = useState(null);
    const editorStateRef = useRef();

    useEffect(() => {
        editorStateRef.current = editorState;
        handleUpdateToolbarPosition();
    }, [editorState]);

    useEffect(() => {
        if (visible) {
            setEditingEntity(null);
            handleUpdateToolbarPosition();
        }
    }, [visible]);

    /**
     * Update the toolbar position
     * @private
     */
    const handleUpdateToolbarPosition = () => {
        clearTimeout(updatePositionTimeout);
        updatePositionTimeout = setTimeout(() => {
            const textSelection = getVisibleSelectionRect(window);
            const focusNode = window.getSelection()?.focusNode;
            const caretNode = focusNode?.tagName ? focusNode : focusNode?.parentNode;
            const containerTop = getContainerEl()?.getBoundingClientRect().top;
            const textSelectionOffsetTop = textSelection && containerTop ? textSelection?.top - containerTop : 0;

            // Avoid updating toolbar position if it has focus within
            if (toolbarRef.current?.contains(document.activeElement)) return;

            const caretBounds = caretNode?.getBoundingClientRect
                ? caretNode.getBoundingClientRect()
                : null

            const top = textSelection ?
                textSelectionOffsetTop + (textSelection?.height || 0) : (caretNode?.offsetTop || 0) + (caretBounds?.height || 0);

            setToolbarStyle({
                ...toolbarStyle,
                top: `${(top || 0) + 10}px`
            });
        }, 10);
    };

    return visible ? (
        <div
            ref={toolbarRef}
            className={classNames(styles.toolbar, {
                [styles.visible]: visible
            })}
            style={toolbarStyle}
        >
            <div className={styles.wrapper}>
                {/* Content */}
                <div className={styles.content}>
                    {editingEntity
                        ? React.createElement(getEditingEntityComponent(editingEntity), {
                              editorState,
                              setEditingEntity,
                              onChange
                          })
                        : actions &&
                          actions.map((groupActions, index) => (
                              <ToolbarItemGroup key={index}>
                                  {groupActions
                                      .filter(actionKey => !excludeActions.includes(actionKey))
                                      .map((actionKey) => {
                                          // Check if action
                                          if (!toolbarActionMap.hasOwnProperty(actionKey)) {
                                              console.warn("MarkupEditor: No such action: %s", actionKey);
                                              return null;
                                          }

                                          const action = toolbarActionMap[actionKey];
                                          let elementType;
                                          if (action.hasOwnProperty("blockType")) {
                                              elementType = BlockTypeAction;
                                          } else if (action.hasOwnProperty("inlineStyle")) {
                                              elementType = InlineStyleAction;
                                          } else if (action.hasOwnProperty("blockStyle")) {
                                              elementType = BlockStyleAction;
                                          } else if (action.hasOwnProperty("entityType")) {
                                              elementType = CustomAction;
                                          }

                                          return React.createElement(elementType, {
                                              key: actionKey,
                                              editorState,
                                              onChange,
                                              setEditingEntity,
                                              ...action
                                          });
                                      })}
                              </ToolbarItemGroup>
                          ))}
                </div>
            </div>
        </div>
    ) : null;
});

const getEditingEntityComponent = (entityType) => {
    switch (entityType) {
        case "LINK":
            return LinkEdit;
        case "TEXT_COLOR":
            return TextColorEdit;
        case "INSERT_SYMBOL":
            return InsertSymbolEdit;
        case "INSERT_DASH":
            return InsertDash;
        default:
            return null;
    }
};
